import React, { useEffect, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ArrowLeftIcon,
  ArrowRightIcon,
  ArrowUpTrayIcon,
  InformationCircleIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { toast } from 'react-toastify';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { createTest, uploadTestAdditionalInformation } from "../../services/Exams/ExamsService";
import KaTeXComponent from "../../components/KaTeXComponent/KaTeXComponent";
import HelpButton from "../../components/Help/HelpButton";
import TestsGuide from "../../components/Help/guides/TestsGuide";
import usePassagesAvailable from "../../components/Passages/usePassagesAvailable";
import { getApiErrorMessage } from "../../utils/apiError";
import { backLinkClass, quietButtonClass } from "./TestBuilder/ui";
import TestHeadingField, { headingErrorText } from "./TestBuilder/TestHeadingField";

// Shared presentation classes for this page
const fieldClass =
  'block w-full rounded-lg px-3 text-sm text-slate-900 shadow-sm transition duration-200 placeholder:text-slate-400 focus:ring-4 disabled:cursor-not-allowed disabled:bg-slate-100 disabled:text-slate-500';
const fieldOk = 'border-slate-300 hover:border-slate-400 focus:border-blue-600 focus:ring-blue-600/15';
const fieldError = 'border-red-300 hover:border-red-400 focus:border-red-500 focus:ring-red-500/15';
const quillClass =
  'rounded-lg bg-white shadow-sm [&_.ql-container]:!border-slate-300 [&_.ql-container]:rounded-b-lg [&_.ql-editor]:min-h-[8rem] [&_.ql-editor]:text-sm [&_.ql-toolbar]:!border-slate-300 [&_.ql-toolbar]:rounded-t-lg [&_.ql-toolbar]:bg-slate-50';

// "1 Basic info → 2 Questions": creating the test is the first of two steps.
function Steps() {
  const [t] = useTranslation();
  return (
    <ol className="mb-4 flex flex-wrap items-center gap-2 text-sm" aria-label={t('test_bank.single_create.steps_label')}>
      <li className="inline-flex items-center gap-2 font-medium text-blue-700" aria-current="step">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-blue-600 text-xs font-semibold text-white">1</span>
        {t('test_bank.single_create.step_details')}
      </li>
      <li aria-hidden="true" className="h-px w-6 bg-slate-300" />
      <li className="inline-flex items-center gap-2 text-slate-500">
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-white text-xs font-semibold text-slate-500 ring-1 ring-inset ring-slate-300">2</span>
        {t('test_bank.single_create.step_questions')}
      </li>
    </ol>
  );
}

const TestCreate = () => {
  const [t] = useTranslation();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    description: '',
    heading: '',
    calculator_is_allowed: false,
    periodic_table_is_allowed: false,
    additional_information: '',
  });
  const [validationErrors, setValidationErrors] = useState({});
  // After a refused submit the first refused field takes the focus, once the form is enabled again (it scrolls
  // into view: the page heading can be off screen on a phone).
  const [errorFocus, setErrorFocus] = useState(0);
  const formRef = useRef(null);
  const [aiFile, setAiFile] = useState(null);
  const aiFileInputRef = useRef(null);
  const passages = usePassagesAvailable();

  useEffect(() => {
    if (errorFocus) formRef.current?.querySelector('[aria-invalid="true"]')?.focus();
  }, [errorFocus]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear validation error when field changes
    if (validationErrors[name]) {
      setValidationErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  // Only the name is required (the description is optional, as in the edit dialog and on the server).
  const validateForm = () => {
    const errors = {};
    if (!formData.name.trim()) {
      errors.name = t('test_bank.form.name_required');
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleAiFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.docx')) {
      toast.error(t('test_bank.form.only_docx'));
      e.target.value = '';
      return;
    }
    const hasContent =
      formData.additional_information &&
      formData.additional_information.replace(/<[^>]*>/g, '').trim();
    if (hasContent) {
      const confirmed = window.confirm(
        t('test_bank.form.confirm_replace_manual'),
      );
      if (!confirmed) {
        e.target.value = '';
        return;
      }
    }
    setAiFile(file);
  };

  const handleClearAiFile = () => {
    setAiFile(null);
    if (aiFileInputRef.current) aiFileInputRef.current.value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      setErrorFocus((n) => n + 1);
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const testData = {
        name: formData.name.trim(),
        description: formData.description.trim(),
        heading: formData.heading.trim(),
        calculator_is_allowed: formData.calculator_is_allowed,
        periodic_table_is_allowed: formData.periodic_table_is_allowed,
        additional_information: aiFile ? '' : formData.additional_information,
      };

      const response = await createTest({ data: testData });

      if (aiFile) {
        try {
          await uploadTestAdditionalInformation({
            params: { uuid: response.uuid },
            file: aiFile,
          });
        } catch (uploadErr) {
          console.error('Failed to upload additional information:', uploadErr);
          toast.error(t('test_bank.form.upload_failed_after_create'));
        }
      }

      // Straight to adding questions (the builder greets with a "test created" banner).
      // replace: Back from there must not reopen this filled form.
      navigate(`/test/${response.uuid}`, { replace: true, state: { justCreated: true, from: '/test_list' } });
    } catch (err) {
      console.error('Error creating test:', err);
      const data = err?.response?.data;
      if (data && typeof data === 'object' && !data.code && (data.name || data.description || data.heading)) {
        setValidationErrors({
          name: [].concat(data.name || []).join(' '),
          description: [].concat(data.description || []).join(' '),
          heading: headingErrorText(t, data.heading),
        });
        setErrorFocus((n) => n + 1);
      } else {
        setError(getApiErrorMessage(t, err, 'test_bank.single_create.create_error'));
      }
      setLoading(false);
    }
  };

  return (
    <div className="motion-safe:animate-fade-in">
      <div className="mx-auto flex max-w-2xl flex-col">
        {/* The way back to the list (the form has no Cancel of its own: it would do the same). */}
        <Link to="/test_list" className={`${backLinkClass} -ml-1.5 mb-3`}>
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" />
          {t('test_bank.single_create.back_to_list')}
        </Link>
        <Steps />
        <div className="rounded-2xl border border-slate-200 bg-white shadow-sm">
          {/* Header, with the guide's "?" right after the title */}
          <div className="border-b border-slate-200 p-5 sm:p-6">
            <div className="flex min-w-0 items-center gap-1.5">
              <h1 className="min-w-0 text-2xl font-semibold tracking-tight text-slate-900">{t('test_bank.form.create_new_test')}</h1>
              <HelpButton id="tests" title={t('help.tests.title')} className="-my-1.5 md:-my-0.5">
                <TestsGuide passages={passages} />
              </HelpButton>
            </div>
            <p className="mt-1 text-sm text-slate-500">{t('test_bank.single_create.subtitle')}</p>
          </div>

          {/* Error Alert */}
          {error && (
            <div role="alert" className="mx-5 mt-5 flex items-center gap-3 rounded-xl border border-red-200 bg-red-50 p-4 sm:mx-6 sm:mt-6">
              <XMarkIcon className="h-5 w-5 flex-shrink-0 text-red-500" aria-hidden="true" />
              <div className="text-sm text-red-700">{error}</div>
              <button
                type="button"
                onClick={() => setError(null)}
                className="ml-auto rounded-md p-1 text-red-400 transition duration-200 hover:bg-red-100 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-600"
                aria-label={t('test_bank.common.dismiss')}
              >
                <XMarkIcon className="h-4 w-4" aria-hidden="true" />
              </button>
            </div>
          )}

          {/* Form */}
          <form ref={formRef} onSubmit={handleSubmit} noValidate className="space-y-6 p-5 sm:p-6">
            {/* Test name */}
            <div>
              <label htmlFor="name" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('test_bank.form.test_name')} <span className="text-red-600" aria-hidden="true">*</span>
              </label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleInputChange}
                placeholder={t('test_bank.form.test_name_placeholder')}
                autoFocus
                required
                aria-invalid={!!validationErrors.name}
                aria-describedby={validationErrors.name ? 'name-error' : undefined}
                className={`${fieldClass} h-10 ${
                  validationErrors.name
                    ? fieldError
                    : fieldOk
                }`}
                disabled={loading}
              />
              {validationErrors.name && (
                <p id="name-error" className="mt-1.5 text-sm text-red-600">{validationErrors.name}</p>
              )}
            </div>

            {/* Test description */}
            <div>
              <label htmlFor="description" className="mb-1.5 block text-sm font-medium text-slate-700">
                {t('test_bank.form.test_description')}{' '}
                <span className="font-normal text-slate-500">({t('test_bank.form.optional')})</span>
              </label>
              <textarea
                id="description"
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                placeholder={t('test_bank.form.description_placeholder')}
                aria-invalid={!!validationErrors.description}
                className={`${fieldClass} resize-none py-2 ${
                  validationErrors.description
                    ? fieldError
                    : fieldOk
                }`}
                disabled={loading}
              />
              {validationErrors.description && (
                <p className="mt-1.5 text-sm text-red-600">{validationErrors.description}</p>
              )}
            </div>

            {/* What the exam's Word prints at the top of the test's pages */}
            <TestHeadingField
              id="heading"
              value={formData.heading}
              onChange={handleInputChange}
              error={validationErrors.heading}
              disabled={loading}
            />

            {/* Calculator toggle */}
            <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
              <input
                type="checkbox"
                checked={formData.calculator_is_allowed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    calculator_is_allowed: e.target.checked,
                  }))
                }
                disabled={loading}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-slate-700">{t('test_bank.form.calculator_allowed')}</span>
            </label>

            {/* Periodic table toggle */}
            <label className="flex cursor-pointer select-none items-center gap-3 rounded-xl border border-slate-200 bg-slate-50/60 px-4 py-3 transition duration-200 hover:border-slate-300">
              <input
                type="checkbox"
                checked={formData.periodic_table_is_allowed}
                onChange={(e) =>
                  setFormData((prev) => ({
                    ...prev,
                    periodic_table_is_allowed: e.target.checked,
                  }))
                }
                disabled={loading}
                className="h-4 w-4 rounded border-slate-300 text-blue-600 transition focus:ring-2 focus:ring-blue-600/30 focus:ring-offset-0"
              />
              <span className="text-sm font-medium text-slate-700">{t('test_bank.form.periodic_table_allowed')}</span>
            </label>

            {/* Additional information */}
            <div>
              <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
                <span className="block text-sm font-medium text-slate-700">
                  {t('test_bank.form.additional_information')}{' '}
                  <span className="font-normal text-slate-500">({t('test_bank.form.optional')})</span>
                </span>
                <div className="flex min-w-0 items-center gap-2">
                  {aiFile && (
                    <span className="flex min-w-0 items-center gap-1 rounded-full bg-slate-100 py-0.5 pl-2.5 pr-1 text-xs font-medium text-slate-700">
                      <span className="max-w-[200px] truncate">{aiFile.name}</span>
                      <button
                        type="button"
                        onClick={handleClearAiFile}
                        disabled={loading}
                        className="flex h-5 w-5 items-center justify-center rounded-full text-sm leading-none text-slate-400 transition duration-200 hover:bg-red-100 hover:text-red-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-1 focus-visible:outline-red-600 disabled:opacity-50"
                        aria-label={t('test_bank.form.remove_file')}
                      >
                        ×
                      </button>
                    </span>
                  )}
                  {/* A secondary way to fill it in: quiet. */}
                  <button
                    type="button"
                    onClick={() => aiFileInputRef.current?.click()}
                    disabled={loading}
                    className={`${quietButtonClass} -mr-3`}
                  >
                    <ArrowUpTrayIcon className="h-4 w-4" aria-hidden="true" />
                    {aiFile ? t('test_bank.form.replace_file') : t('test_bank.form.upload_docx')}
                  </button>
                  <input
                    ref={aiFileInputRef}
                    type="file"
                    accept=".docx"
                    onChange={handleAiFileChange}
                    className="hidden"
                  />
                </div>
              </div>
              <ReactQuill
                theme="snow"
                value={formData.additional_information}
                onChange={(value) =>
                  setFormData((prev) => ({ ...prev, additional_information: value }))
                }
                readOnly={loading || Boolean(aiFile)}
                className={quillClass}
              />
              <p className="mt-1.5 text-xs text-slate-500">
                {aiFile
                  ? t('test_bank.form.file_will_replace')
                  : t('test_bank.form.formulas_hint')}
              </p>
              {!aiFile && formData.additional_information && formData.additional_information.replace(/<[^>]*>/g, '').trim() && (
                <div className="mt-3">
                  <p className="mb-1.5 text-xs font-medium uppercase tracking-wider text-slate-500">{t('test_bank.form.preview')}</p>
                  <div className="max-h-60 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-4 text-sm text-slate-700">
                    <KaTeXComponent text={formData.additional_information} />
                  </div>
                </div>
              )}
            </div>

            {/* Form Actions: the one main button (the back link above leaves without creating) */}
            <div className="flex flex-col gap-3 border-t border-slate-200 pt-5 sm:flex-row sm:items-center sm:justify-end">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-lg bg-blue-600 px-4 text-sm font-medium text-white shadow-sm shadow-blue-600/25 transition duration-200 enabled:hover:bg-blue-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-blue-600 disabled:cursor-not-allowed disabled:opacity-50 disabled:shadow-none sm:h-10"
              >
                {loading ? (
                  <>
                    <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                    {t('test_bank.form.creating')}
                  </>
                ) : (
                  <>
                    {t('test_bank.single_create.submit_and_add')}
                    <ArrowRightIcon className="h-4 w-4" aria-hidden="true" />
                  </>
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Help text */}
        <div className="mt-4 flex gap-3 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
          <InformationCircleIcon className="h-5 w-5 shrink-0 text-blue-600" aria-hidden="true" />
          <p className="text-sm text-blue-800/90">
            {t('test_bank.single_create.tip_text')}
          </p>
        </div>
      </div>
    </div>
  );
};

export default TestCreate;
